import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ObjectivesCombobox, ObjectivesProvider } from '@/components/ui/ObjectivesCombobox';

export function ObjectivesComboboxExample() {
  // State for the first example (with provider)
  const [selectedObjectives1, setSelectedObjectives1] = React.useState<string[]>([]);
  
  // State for the second example (with custom objectives)
  const [selectedObjectives2, setSelectedObjectives2] = React.useState<string[]>([]);
  const [showCustomObjectives, setShowCustomObjectives] = React.useState(false);
  
  // Mock objectives for demonstration
  const mockObjectives = [
    {
      id: 'obj-1',
      objectiveText: 'Implement multi-factor authentication for all systems',
      owner: 'security@example.com',
      addedBy: 'admin@example.com',
      dateAdded: new Date(),
      dateUpdated: new Date(),
      objectiveTargetDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now
      updatedBy: 'admin@example.com',
    },
    {
      id: 'obj-2',
      objectiveText: 'Conduct security awareness training for all employees',
      owner: 'hr@example.com',
      addedBy: 'admin@example.com',
      dateAdded: new Date(),
      dateUpdated: new Date(),
      objectiveTargetDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000), // 60 days from now
      updatedBy: 'admin@example.com',
    },
    {
      id: 'obj-3',
      objectiveText: 'Complete ISO 27001 certification',
      owner: 'compliance@example.com',
      addedBy: 'admin@example.com',
      dateAdded: new Date(),
      dateUpdated: new Date(),
      objectiveTargetDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // 90 days from now
      updatedBy: 'admin@example.com',
    },
  ];

  // Handle objective selection/deselection
  const handleObjectiveSelect = (objectiveId: string) => {
    console.log('Objective selected:', objectiveId);
  };

  const handleObjectiveDeselect = (objectiveId: string) => {
    console.log('Objective deselected:', objectiveId);
  };

  // Toggle custom objectives example
  const toggleCustomObjectives = () => {
    setShowCustomObjectives(!showCustomObjectives);
    if (!showCustomObjectives) {
      setSelectedObjectives2([]);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-4">
      <Card>
        <CardHeader>
          <CardTitle>ObjectivesCombobox Examples</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Example 1: Using ObjectivesProvider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="objectives-1">Example 1: Using ObjectivesProvider</Label>
              <div className="flex items-center space-x-2">
                <Badge variant="outline">
                  {selectedObjectives1.length} selected
                </Badge>
                {selectedObjectives1.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedObjectives1([])}
                    className="h-6 px-2 text-xs text-destructive"
                  >
                    Clear all
                  </Button>
                )}
              </div>
            </div>
            
            <ObjectivesProvider>
              <ObjectivesCombobox
                selected={selectedObjectives1}
                setSelected={setSelectedObjectives1}
                onObjectiveSelect={handleObjectiveSelect}
                onObjectiveDeselect={handleObjectiveDeselect}
                className="w-full"
                placeholder="Select objectives..."
              />
            </ObjectivesProvider>
            
            <div className="mt-2 text-sm text-muted-foreground">
              This example uses the <code className="bg-muted px-1 rounded">ObjectivesProvider</code> to automatically fetch and manage objectives.
            </div>
          </div>
          
          <div className="border-t my-6" />
          
          {/* Example 2: With custom objectives */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-custom" 
                  checked={showCustomObjectives}
                  onCheckedChange={toggleCustomObjectives}
                />
                <Label htmlFor="show-custom">Show Custom Objectives Example</Label>
              </div>
              
              {showCustomObjectives && (
                <div className="flex items-center space-x-2">
                  <Badge variant="outline">
                    {selectedObjectives2.length} selected
                  </Badge>
                  {selectedObjectives2.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedObjectives2([])}
                      className="h-6 px-2 text-xs text-destructive"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {showCustomObjectives && (
              <>
                <ObjectivesCombobox
                  selected={selectedObjectives2}
                  setSelected={setSelectedObjectives2}
                  objectives={mockObjectives}
                  className="w-full"
                  placeholder="Select custom objectives..."
                />
                
                <div className="mt-2 p-4 bg-muted/50 rounded-md">
                  <h4 className="font-medium mb-2">Selected Objectives:</h4>
                  {selectedObjectives2.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedObjectives2.map(id => {
                        const objective = mockObjectives.find(obj => obj.id === id);
                        if (!objective) return null;
                        
                        return (
                          <li 
                            key={id}
                            className="flex items-start justify-between p-2 bg-background rounded border"
                          >
                            <div>
                              <div className="font-medium">{objective.objectiveText}</div>
                              <div className="text-xs text-muted-foreground">
                                Target: {new Date(objective.objectiveTargetDate).toLocaleDateString()}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedObjectives2(prev => 
                                  prev.filter(item => item !== id)
                                );
                              }}
                              className="h-6 w-6 p-0 text-destructive"
                            >
                              ×
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No objectives selected</p>
                  )}
                </div>
                
                <div className="mt-2 text-sm text-muted-foreground">
                  This example passes objectives directly to the combobox without using the provider.
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Code Example */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Examples</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-2">Basic Usage with Provider:</h3>
              <pre className="bg-muted p-4 rounded-md overflow-x-auto text-sm">
                {`import { ObjectivesCombobox, ObjectivesProvider } from '@/components/ui/ObjectivesCombobox';

function Example() {
  const [selected, setSelected] = React.useState<string[]>([]);
  
  return (
    <ObjectivesProvider>
      <ObjectivesCombobox
        selected={selected}
        setSelected={setSelected}
        placeholder="Select objectives..."
      />
    </ObjectivesProvider>
  );
}`}
              </pre>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">With Custom Objectives:</h3>
              <pre className="bg-muted p-4 rounded-md overflow-x-auto text-sm">
                {`import { ObjectivesCombobox } from '@/components/ui/ObjectivesCombobox';

const customObjectives = [
  {
    id: '1',
    objectiveText: 'Custom Objective 1',
    owner: 'user@example.com',
    // ... other required fields
  },
  // ... more objectives
];

function Example() {
  const [selected, setSelected] = React.useState<string[]>([]);
  
  return (
    <ObjectivesCombobox
      selected={selected}
      setSelected={setSelected}
      objectives={customObjectives}
      placeholder="Select custom objectives..."
    />
  );
}`}
              </pre>
            </div>
            
            <div>
              <h3 className="font-medium mb-2">With Callbacks:</h3>
              <pre className="bg-muted p-4 rounded-md overflow-x-auto text-sm">
                {`<ObjectivesCombobox
  selected={selected}
  setSelected={setSelected}
  onObjectiveSelect={(id) => console.log('Selected:', id)}
  onObjectiveDeselect={(id) => console.log('Deselected:', id)}
  onAllSelect={() => console.log('All selected')}
  onAllDeselect={() => console.log('All deselected')}
/>`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ObjectivesComboboxExample;
